import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";

import { DataContext } from "@/provider/DataContextProvider";
import { useSaveBasket } from "@/services/api";
import type { BasicCustomerData } from "@/services/model";
import {
  hasValidPersonalDetails,
  transformBasicCustomerDataToPersonalDetailsForm,
} from "@/utils/customerUtils";
import {
  hasBasketEntryWithNamedDevice,
  hasBasketEntryWithSelectedRateOption,
  hasOpenBasketEntry,
} from "@/utils/dataContextHelpers";

/**
 * This hook returns a callback that checks which is the last onboarding step
 * that the user has previously filled, and redirects them to the immediate
 * step after that one.
 * It also returns its fetch loading state, so that it can be used to handle the
 * UI while the hook is in process.
 */
export const useOnboardingRedirect = (): [
  (customerData: BasicCustomerData) => void,
  boolean,
] => {
  const { dataContext, setDataContext } = useContext(DataContext);
  const navigate = useNavigate();
  const [isRedirecting, setIsRedirecting] = useState(false);

  // Get the query to get or create the customer's basket
  const { mutateAsync: getBasicBasket } = useSaveBasket();

  const redirect = async (customerData: BasicCustomerData) => {
    setIsRedirecting(true); // Only set when actually redirecting
    let dataContextSnapshot = dataContext;

    // Lazily fetch basket only when we actually redirect into onboarding.
    if (!dataContextSnapshot.basket) {
      try {
        const basket = await getBasicBasket({});
        dataContextSnapshot = {
          ...dataContextSnapshot,
          basket,
        };
        setDataContext((prev) => ({
          ...prev,
          basket,
        }));
      } catch {
        setIsRedirecting(false);

        return;
      }
    }

    if (hasValidPersonalDetails(customerData)) {
      setDataContext((prev) => ({
        ...prev,
        personalDetails:
          transformBasicCustomerDataToPersonalDetailsForm(customerData),
        allowedMaxOnboardingStep: 5,
      }));
      navigate("/onboarding/5");
    } else if (hasBasketEntryWithSelectedRateOption(dataContextSnapshot)) {
      setDataContext((prev) => ({
        ...prev,
        allowedMaxOnboardingStep: 4,
      }));
      navigate("/onboarding/4");
    } else if (hasBasketEntryWithNamedDevice(dataContextSnapshot)) {
      setDataContext((prev) => ({
        ...prev,
        allowedMaxOnboardingStep: 3,
      }));
      navigate("/onboarding/3");
    } else if (hasOpenBasketEntry(dataContextSnapshot)) {
      setDataContext((prev) => ({
        ...prev,
        allowedMaxOnboardingStep: 2,
      }));
      navigate("/onboarding/2");
    } else {
      setDataContext((prev) => ({
        ...prev,
      }));
      navigate("/onboarding/1/");
    }

    // Reset after navigation completes
    setIsRedirecting(false);
  };

  return [redirect, isRedirecting];
};
